import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { theaterConfig } from './data';

// Parse California date function (imported from Components.js logic)
const parseCaliforniaDate = (dateString) => {
  if (!dateString) return null;
  
  // Parse date as local California date without timezone conversion
  const parts = dateString.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1; // JavaScript months are 0-indexed
    const day = parseInt(parts[2]);
    
    // Create date in local timezone (California)
    return new Date(year, month, day);
  }
  
  return new Date(dateString);
};

// Helper function to categorize and sort events
const categorizeEvents = (events) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  const currentEvents = [];
  const pastEvents = [];
  
  events.forEach(event => {
    const eventDate = parseCaliforniaDate(event.date);
    if (eventDate && eventDate >= today) {
      currentEvents.push(event);
    } else {
      pastEvents.push(event);
    }
  });
  
  // Sort current events: earliest first
  currentEvents.sort((a, b) => parseCaliforniaDate(a.date) - parseCaliforniaDate(b.date));
  
  // Sort past events: most recent first
  pastEvents.sort((a, b) => parseCaliforniaDate(b.date) - parseCaliforniaDate(a.date));
  
  return { currentEvents, pastEvents };
};

// Simple Admin Interface for managing events and contacts
// This would typically be secured behind authentication in a real application

const AdminInterface = ({ events, setEvents, deleteEvent, contacts, setContacts, featuredEvent, setFeaturedEvent, galleryImages, setGalleryImages, pageImages, setPageImages, bannerSettings, setBannerSettings, empressSettings, setEmpressSettings, apiService, isLoading }) => {
  // SECURITY: Input sanitization function
  const sanitizeInput = (input) => {
    if (!input) return '';
    return DOMPurify.sanitize(input, {
      ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'span', 'a', 'strong', 'em', 'ul', 'ol', 'li', 'br'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
      ALLOWED_STYLES: {
        '*': {
          // Allow common text formatting styles
          'color': [/^#[0-9A-F]{3,6}$/i, /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i, /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*[01]?\.?\d*\s*\)$/i],
          'background-color': [/^#[0-9A-F]{3,6}$/i, /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i],
          'font-size': [/^(\d+)(px|em|rem|%|pt)$/i],
          'font-weight': [/^(normal|bold|bolder|lighter|\d{3})$/i],
          'font-family': [/.*/],
          'text-decoration': [/^(none|underline|overline|line-through)$/i],
          'font-style': [/^(normal|italic|oblique)$/i],
          'text-align': [/^(left|right|center|justify)$/i]
        }
      }
    });
  };
  const [showAdmin, setShowAdmin] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [sessionExpiry, setSessionExpiry] = useState(null);
  const [activeTab, setActiveTab] = useState('events');
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: '',
    time: '',
    category: 'Concert',
    ticketPrice: '',
    description: '',
    image: '',
    ticketingAgency: 'Ticketmaster',
    ticketLink: ''
  });
  const [newImageUrl, setNewImageUrl] = useState('');
  const [editingEvent, setEditingEvent] = useState(null);
  const [editEventData, setEditEventData] = useState({
    title: '',
    date: '',
    time: '',
    category: 'Concert',
    ticketPrice: '',
    description: '',
    image: '',
    ticketingAgency: 'Ticketmaster',
    ticketLink: ''
  });
  const [selectedFeaturedEventId, setSelectedFeaturedEventId] = useState(() => {
    const saved = localStorage.getItem('crestTheaterFeaturedEventId');
    return saved || null;
  });

  // Authentication constants
  const ADMIN_PASSWORD_HASH = 'Crest!1013Kstreet'; // In production, this should be properly hashed
  const SESSION_DURATION = 60 * 60 * 1000; // 60 minutes in milliseconds

  // Check for existing session on component mount
  useEffect(() => {
    const savedSession = localStorage.getItem('crestTheaterAdminSession');
    if (savedSession) {
      const sessionData = JSON.parse(savedSession);
      const now = new Date().getTime();
      if (now < sessionData.expiry) {
        setIsAuthenticated(true);
        setSessionExpiry(sessionData.expiry);
      } else {
        localStorage.removeItem('crestTheaterAdminSession');
      }
    }

    // Listen for CTRL+A admin access
    const handleToggleAdmin = () => {
      toggleAdmin();
    };

    window.addEventListener('toggleAdmin', handleToggleAdmin);
    return () => {
      window.removeEventListener('toggleAdmin', handleToggleAdmin);
    };
  }, []);

  // Auto-logout when session expires
  useEffect(() => {
    if (sessionExpiry) {
      const timeUntilExpiry = sessionExpiry - new Date().getTime();
      if (timeUntilExpiry > 0) {
        const timeout = setTimeout(() => {
          handleLogout();
        }, timeUntilExpiry);
        return () => clearTimeout(timeout);
      }
    }
  }, [sessionExpiry]);

  // Handle login
  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD_HASH) {
      const expiry = new Date().getTime() + SESSION_DURATION;
      setIsAuthenticated(true);
      setSessionExpiry(expiry);
      setShowLoginForm(false);
      setShowAdmin(true);
      setPassword('');
      setLoginError('');
      
      // Save session to localStorage
      localStorage.setItem('crestTheaterAdminSession', JSON.stringify({
        expiry: expiry
      }));
    } else {
      setLoginError('Invalid password. Please try again.');
      setPassword('');
    }
  };

  // Handle logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    setSessionExpiry(null);
    setShowAdmin(false);
    setShowLoginForm(false);
    setPassword('');
    setLoginError('');
    localStorage.removeItem('crestTheaterAdminSession');
  };

  // Modified toggle admin to check authentication
  const toggleAdmin = () => {
    if (isAuthenticated) {
      setShowAdmin(!showAdmin);
    } else {
      setShowLoginForm(true);
    }
  };

  // Add new event
  const handleAddEvent = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare event data for API (matching backend model)
      const eventData = {
        title: newEvent.title,
        description: newEvent.description,
        date: newEvent.date,
        time: newEvent.time || "",
        category: newEvent.category,
        image_url: newEvent.image,
        ticket_price: newEvent.ticketPrice || "",
        ticket_url: newEvent.ticketLink || null,
        ticketing_agency: newEvent.ticketingAgency || 'Ticketmaster'
      };

      // Create event via API
      const createdEvent = await apiService.createEvent(eventData);
      
      // Update local state with the created event
      const eventToAdd = {
        id: createdEvent.id,
        title: createdEvent.title,
        date: createdEvent.date,
        time: createdEvent.time,
        category: createdEvent.category,
        ticketPrice: createdEvent.ticket_price,
        description: createdEvent.description,
        image: createdEvent.image_url,
        ticketingAgency: createdEvent.ticketing_agency,
        ticketLink: createdEvent.ticket_url
      };
      
      setEvents(prevEvents => [...prevEvents, eventToAdd]);
      
      // Reset form
      setNewEvent({
        title: '',
        date: '',
        time: '',
        category: 'Concert',
        ticketPrice: '',
        description: '',
        image: '',
        ticketingAgency: 'Ticketmaster',
        ticketLink: ''
      });
      
      alert('✅ Event added successfully and saved to database!');
      console.log('✅ Event created via API:', createdEvent);
      
    } catch (error) {
      console.error('❌ Failed to create event via API:', error);
      
      // Fallback to local storage
      const eventToAdd = {
        ...newEvent,
        id: Date.now()
      };
      setEvents(prevEvents => [...prevEvents, eventToAdd]);
      
      // Reset form
      setNewEvent({
        title: '',
        date: '',
        time: '',
        category: 'Concert',
        ticketPrice: '',
        description: '',
        image: '',
        ticketingAgency: 'Ticketmaster',
        ticketLink: ''
      });
      
      alert('⚠️ Event added locally (API connection failed). Changes will sync when connection is restored.');
    }
  };

  // Delete event
  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      try {
        const result = await deleteEvent(eventId);
        if (result) {
          alert('✅ Event deleted successfully!');
        } else {
          alert('⚠️ Event removed from display but there may have been a database error.');
        }
      } catch (error) {
        console.error('Error deleting event:', error);
        alert('❌ Failed to delete event. Please try again.');
      }
    }
  };

  // Start editing an event
  const handleEditEvent = (event) => {
    setEditingEvent(event.id);
    setEditEventData({
      title: event.title,
      date: event.date,
      time: event.time,
      category: event.category,
      ticketPrice: event.ticketPrice,
      description: event.description,
      image: event.image,
      ticketingAgency: event.ticketingAgency || 'Ticketmaster',
      ticketLink: event.ticketLink || ''
    });
  };

  // Save edited event
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare event data for API (matching backend model)
      const eventUpdateData = {
        title: editEventData.title,
        description: editEventData.description,
        date: editEventData.date,
        time: editEventData.time || "",
        category: editEventData.category,
        image_url: editEventData.image,
        ticket_price: editEventData.ticketPrice || "",
        ticket_url: editEventData.ticketLink || null,
        ticketing_agency: editEventData.ticketingAgency || 'Ticketmaster'
      };

      // Update event via API
      const updatedEvent = await apiService.updateEvent(editingEvent, eventUpdateData);
      
      // Update local state with the updated event
      const eventToUpdate = {
        id: updatedEvent.id,
        title: updatedEvent.title,
        date: updatedEvent.date,
        time: updatedEvent.time,
        category: updatedEvent.category,
        ticketPrice: updatedEvent.ticket_price,
        description: updatedEvent.description,
        image: updatedEvent.image_url,
        ticketingAgency: updatedEvent.ticketing_agency,
        ticketLink: updatedEvent.ticket_url
      };
      
      setEvents(prevEvents => 
        prevEvents.map(event => 
          event.id === editingEvent 
            ? eventToUpdate
            : event
        )
      );
      
      setEditingEvent(null);
      alert('✅ Event updated successfully and saved to database!');
      console.log('✅ Event updated via API:', updatedEvent);
      
    } catch (error) {
      console.error('❌ Failed to update event via API:', error);
      
      // Fallback to local storage update
      setEvents(prevEvents => 
        prevEvents.map(event => 
          event.id === editingEvent 
            ? { ...event, ...editEventData }
            : event
        )
      );
      setEditingEvent(null);
      alert('⚠️ Event updated locally but failed to save to database. Changes may not persist.');
    }
  };

  // Handle featured event selection
  const handleFeaturedEventChange = (eventId) => {
    setSelectedFeaturedEventId(eventId);
    localStorage.setItem('crestTheaterFeaturedEventId', eventId);
    
    // Find the selected event and update featured event
    if (eventId) {
      const selectedEvent = events.find(event => event.id.toString() === eventId);
      if (selectedEvent) {
        setFeaturedEvent(selectedEvent);
        alert('Featured event updated successfully!');
      }
    } else {
      // Reset to default (next event)
      const nextEvent = getNextEvent();
      setFeaturedEvent(nextEvent);
      localStorage.removeItem('crestTheaterFeaturedEventId');
      alert('Featured event reset to next upcoming event!');
    }
  };

  // Get next upcoming event (chronologically)
  const getNextEvent = () => {
    const now = new Date();
    const upcomingEvents = events
      .filter(event => new Date(event.date) >= now)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    
    return upcomingEvents.length > 0 ? upcomingEvents[0] : events[0];
  };

  // Handle page image changes
  const handlePageImageChange = (imageType, imageUrl) => {
    const updatedPageImages = {
      ...pageImages,
      [imageType]: imageUrl
    };
    
    // Update the parent component's page images state (will handle API call)
    setPageImages(updatedPageImages);
    
    alert(`✅ ${imageType.charAt(0).toUpperCase() + imageType.slice(1)} image updated and saved to database!`);
  };

  // Handle banner setting changes
  const handleBannerChange = (setting, value) => {
    const updatedBannerSettings = {
      ...bannerSettings,
      [setting]: value
    };
    
    setBannerSettings(updatedBannerSettings);
  };

  // Handle empress settings changes
  // SECURITY: Handle Empress Settings changes with sanitization
  const handleEmpressChange = (setting, value) => {
    // Don't sanitize during editing - only sanitize on display
    // This allows users to work with rich formatting that gets sanitized when displayed
    const updatedEmpressSettings = {
      ...empressSettings,
      [setting]: value  // Store raw value, sanitize only on display
    };
    
    setEmpressSettings(updatedEmpressSettings);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingEvent(null);
    setEditEventData({
      title: '',
      date: '',
      time: '',
      category: 'Concert',
      ticketPrice: '',
      description: '',
      image: ''
    });
  };

  // Add gallery image with validation
  const handleAddImage = (e) => {
    e.preventDefault();
    if (newImageUrl.trim()) {
      // Basic URL validation
      const url = newImageUrl.trim();
      const isValidImageUrl = /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url) || url.includes('imgur.com') || url.includes('unsplash.com') || url.includes('pexels.com');
      
      if (!isValidImageUrl) {
        alert('⚠️ Warning: This URL might not be a direct image link. Make sure it ends in .jpg, .png, .gif, or .webp for best results.');
      }
      
      // Create new array with added image
      const newGalleryImages = [...galleryImages, url];
      setGalleryImages(newGalleryImages);
      setNewImageUrl('');
      alert('✅ Image added to gallery and saved to database!');
    }
  };

  // Remove gallery image
  const handleRemoveImage = (index) => {
    if (window.confirm('Are you sure you want to remove this image from the gallery?')) {
      // Create new array with removed image
      const newGalleryImages = galleryImages.filter((_, i) => i !== index);
      setGalleryImages(newGalleryImages);
      alert('✅ Image removed and changes saved to database!');
    }
  };

  // Reset gallery to default
  const handleResetGallery = () => {
    if (window.confirm('Are you sure you want to reset the gallery to default images? This will remove all your custom additions.')) {
      setGalleryImages(theaterConfig.galleryImages);
      alert('✅ Gallery reset to default images and saved!');
    }
  };

  // Update contact information
  const handleContactUpdate = (field, value) => {
    let updatedContacts;
    
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      updatedContacts = {
        ...contacts,
        [parent]: {
          ...contacts[parent],
          [child]: value
        }
      };
    } else {
      updatedContacts = {
        ...contacts,
        [field]: value
      };
    }
    
    // Call the API-aware setContacts function (which is actually updateContacts from App.js)
    setContacts(updatedContacts);
  };

  // Login Form Component
  if (showLoginForm && !isAuthenticated) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg w-full max-w-md">
          {/* Header */}
          <div className="bg-gray-800 text-white p-4 flex justify-between items-center rounded-t-lg">
            <h2 className="text-xl font-bold">🔐 Admin Login</h2>
            <button
              onClick={() => setShowLoginForm(false)}
              className="text-white hover:text-gray-300 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Login Form */}
          <div className="p-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter admin password"
                  autoFocus
                  required
                />
              </div>

              {loginError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {loginError}
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-gray-800 text-white py-3 px-4 rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                🔓 Login to Admin Panel
              </button>
            </form>

            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                Session expires after 60 minutes of inactivity
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!showAdmin) {
    return (
      <button
        onClick={toggleAdmin}
        className="fixed bottom-4 left-4 bg-gray-800 text-white p-2 rounded-full shadow-lg hover:bg-gray-700 transition-colors"
        style={{ zIndex: 9999, fontSize: '18px', width: '45px', height: '45px' }}
      >
        🔧
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gray-800 text-white p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">Theater Admin Panel</h2>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-300">
              Session: {Math.round((sessionExpiry - new Date().getTime()) / 60000)}m remaining
            </span>
            <button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
            >
              🚪 Logout
            </button>
            <button
              onClick={toggleAdmin}
              className="text-white hover:text-gray-300 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex flex-wrap md:flex-nowrap gap-2 md:gap-8 p-2 md:p-0 overflow-x-auto">
            <button
              onClick={() => setActiveTab('events')}
              className={`py-2 px-3 md:px-4 border-b-2 font-medium text-xs md:text-sm whitespace-nowrap ${
                activeTab === 'events'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Manage Events
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-2 px-3 md:px-4 border-b-2 font-medium text-xs md:text-sm whitespace-nowrap ${
                activeTab === 'gallery'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Gallery Images
            </button>
            <button
              onClick={() => setActiveTab('pageImages')}
              className={`py-2 px-3 md:px-4 border-b-2 font-medium text-xs md:text-sm whitespace-nowrap ${
                activeTab === 'pageImages'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Page Images
            </button>
            <button
              onClick={() => setActiveTab('banner')}
              className={`py-2 px-3 md:px-4 border-b-2 font-medium text-xs md:text-sm whitespace-nowrap ${
                activeTab === 'banner'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Banner
            </button>
            <button
              onClick={() => setActiveTab('featured')}
              className={`py-2 px-3 md:px-4 border-b-2 font-medium text-xs md:text-sm whitespace-nowrap ${
                activeTab === 'featured'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Featured Event
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`py-2 px-3 md:px-4 border-b-2 font-medium text-xs md:text-sm whitespace-nowrap ${
                activeTab === 'contacts'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Contact Info
            </button>
            <button
              onClick={() => setActiveTab('empress')}
              className={`py-2 px-3 md:px-4 border-b-2 font-medium text-xs md:text-sm whitespace-nowrap ${
                activeTab === 'empress'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Empress Tavern
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-3 md:p-6 overflow-y-auto max-h-[70vh]">
          {activeTab === 'events' && (
            <div>
              {/* Add New Event Form */}
              <div className="mb-8 bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-bold mb-4">Add New Event</h3>
                <form onSubmit={handleAddEvent} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={newEvent.title}
                      onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      value={newEvent.date}
                      onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                    <input
                      type="text"
                      value={newEvent.time}
                      onChange={(e) => setNewEvent({...newEvent, time: e.target.value})}
                      placeholder="7:00 PM"
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={newEvent.category}
                      onChange={(e) => setNewEvent({...newEvent, category: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded"
                    >
                      <option value="Concert">Concert</option>
                      <option value="Comedy">Comedy</option>
                      <option value="Theater">Theater</option>
                      <option value="Special Event">Special Event</option>
                      <option value="Movie">Movie</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Price</label>
                    <input
                      type="text"
                      value={newEvent.ticketPrice}
                      onChange={(e) => setNewEvent({...newEvent, ticketPrice: e.target.value})}
                      placeholder="$50"
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                    <input
                      type="url"
                      value={newEvent.image}
                      onChange={(e) => setNewEvent({...newEvent, image: e.target.value})}
                      placeholder="https://..."
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>
                  <div className="col-span-1 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={newEvent.description}
                      onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded"
                      rows="3"
                    />
                  </div>
                  <div className="col-span-1 md:col-span-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Ticketing Agency</label>
                        <select
                          value={newEvent.ticketingAgency}
                          onChange={(e) => setNewEvent({...newEvent, ticketingAgency: e.target.value, ticketLink: e.target.value === 'Other' ? newEvent.ticketLink : ''})}
                          className="w-full p-2 border border-gray-300 rounded"
                        >
                          <option value="Ticketmaster">Ticketmaster</option>
                          <option value="Eventbrite">Eventbrite</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Ticket Link URL {newEvent.ticketingAgency === 'Other' && <span className="text-red-500">*</span>}
                        </label>
                        <input
                          type="url"
                          value={newEvent.ticketLink}
                          onChange={(e) => setNewEvent({...newEvent, ticketLink: e.target.value})}
                          placeholder={
                            newEvent.ticketingAgency === 'Other' 
                              ? "https://... (Required for Other)" 
                              : "Optional - Leave empty to use default"
                          }
                          className="w-full p-2 border border-gray-300 rounded"
                          required={newEvent.ticketingAgency === 'Other'}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <button
                      type="submit"
                      className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 transition-colors"
                    >
                      Add Event
                    </button>
                  </div>
                </form>
              </div>

              {/* Existing Events */}
              <div>
                {(() => {
                  const { currentEvents, pastEvents } = categorizeEvents(events);
                  
                  return (
                    <>
                      {/* Current Events Section */}
                      <div className="mb-8">
                        <h3 className="text-lg font-bold mb-4">Current Events ({currentEvents.length})</h3>
                        <div className="space-y-4">
                          {currentEvents.length === 0 ? (
                            <p className="text-gray-500 italic">No current events found.</p>
                          ) : (
                            currentEvents.map((event) => (
                              <div key={event.id} className="border border-gray-200 rounded-lg p-4">
                                {editingEvent === event.id ? (
                                  /* Edit Form */
                                  <form onSubmit={handleSaveEdit} className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                              <input
                                type="text"
                                value={editEventData.title}
                                onChange={(e) => setEditEventData({...editEventData, title: e.target.value})}
                                className="w-full p-2 border border-gray-300 rounded"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                              <input
                                type="date"
                                value={editEventData.date}
                                onChange={(e) => setEditEventData({...editEventData, date: e.target.value})}
                                className="w-full p-2 border border-gray-300 rounded"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                              <input
                                type="text"
                                value={editEventData.time}
                                onChange={(e) => setEditEventData({...editEventData, time: e.target.value})}
                                placeholder="7:00 PM"
                                className="w-full p-2 border border-gray-300 rounded"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                              <select
                                value={editEventData.category}
                                onChange={(e) => setEditEventData({...editEventData, category: e.target.value})}
                                className="w-full p-2 border border-gray-300 rounded"
                              >
                                <option value="Concert">Concert</option>
                                <option value="Comedy">Comedy</option>
                                <option value="Theater">Theater</option>
                                <option value="Special Event">Special Event</option>
                                <option value="Movie">Movie</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Price</label>
                              <input
                                type="text"
                                value={editEventData.ticketPrice}
                                onChange={(e) => setEditEventData({...editEventData, ticketPrice: e.target.value})}
                                placeholder="$50"
                                className="w-full p-2 border border-gray-300 rounded"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                              <input
                                type="url"
                                value={editEventData.image}
                                onChange={(e) => setEditEventData({...editEventData, image: e.target.value})}
                                placeholder="https://..."
                                className="w-full p-2 border border-gray-300 rounded"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                              value={editEventData.description}
                              onChange={(e) => setEditEventData({...editEventData, description: e.target.value})}
                              className="w-full p-2 border border-gray-300 rounded"
                              rows="3"
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Ticketing Agency</label>
                              <select
                                value={editEventData.ticketingAgency}
                                onChange={(e) => setEditEventData({...editEventData, ticketingAgency: e.target.value, ticketLink: e.target.value === 'Other' ? editEventData.ticketLink : ''})}
                                className="w-full p-2 border border-gray-300 rounded"
                              >
                                <option value="Ticketmaster">Ticketmaster</option>
                                <option value="Eventbrite">Eventbrite</option>
                                <option value="Other">Other</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Ticket Link URL {editEventData.ticketingAgency === 'Other' && <span className="text-red-500">*</span>}
                              </label>
                              <input
                                type="url"
                                value={editEventData.ticketLink}
                                onChange={(e) => setEditEventData({...editEventData, ticketLink: e.target.value})}
                                placeholder={
                                  editEventData.ticketingAgency === 'Other' 
                                    ? "https://... (Required for Other)" 
                                    : "Optional - Leave empty to use default"
                                }
                                className="w-full p-2 border border-gray-300 rounded"
                                required={editEventData.ticketingAgency === 'Other'}
                              />
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              type="submit"
                              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors"
                            >
                              Save Changes
                            </button>
                            <button
                              type="button"
                              onClick={handleCancelEdit}
                              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      ) : (
                        /* Display Mode */
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-semibold">{event.title}</h4>
                            <p className="text-sm text-gray-600">{event.date} at {event.time} | {event.category}</p>
                            <p className="text-sm text-red-600">{event.ticketPrice}</p>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditEvent(event)}
                              className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors text-sm"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteEvent(event.id)}
                              className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition-colors text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                          )}
                        </div>
                      </div>

                      {/* Past Events Section */}
                      {pastEvents.length > 0 && (
                        <div>
                          <h3 className="text-lg font-bold mb-4">Past Events ({pastEvents.length})</h3>
                          <div className="space-y-4">
                            {pastEvents.map((event) => (
                              <div key={event.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                {editingEvent === event.id ? (
                                  /* Edit Form for Past Events */
                                  <form onSubmit={handleSaveEdit} className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                        <input
                                          type="text"
                                          value={editEventData.title}
                                          onChange={(e) => setEditEventData({...editEventData, title: e.target.value})}
                                          className="w-full p-2 border border-gray-300 rounded"
                                          required
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                                        <input
                                          type="date"
                                          value={editEventData.date}
                                          onChange={(e) => setEditEventData({...editEventData, date: e.target.value})}
                                          className="w-full p-2 border border-gray-300 rounded"
                                          required
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                                        <input
                                          type="text"
                                          value={editEventData.time}
                                          onChange={(e) => setEditEventData({...editEventData, time: e.target.value})}
                                          className="w-full p-2 border border-gray-300 rounded"
                                          placeholder="7:00 PM"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                        <select
                                          value={editEventData.category}
                                          onChange={(e) => setEditEventData({...editEventData, category: e.target.value})}
                                          className="w-full p-2 border border-gray-300 rounded"
                                        >
                                          <option value="Concert">Concert</option>
                                          <option value="Comedy">Comedy</option>
                                          <option value="Theater">Theater</option>
                                          <option value="Movie">Movie</option>
                                          <option value="Special Event">Special Event</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Price</label>
                                        <input
                                          type="text"
                                          value={editEventData.ticketPrice}
                                          onChange={(e) => setEditEventData({...editEventData, ticketPrice: e.target.value})}
                                          className="w-full p-2 border border-gray-300 rounded"
                                          placeholder="$25"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                                        <input
                                          type="url"
                                          value={editEventData.image}
                                          onChange={(e) => setEditEventData({...editEventData, image: e.target.value})}
                                          className="w-full p-2 border border-gray-300 rounded"
                                          placeholder="https://example.com/image.jpg"
                                        />
                                      </div>
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                      <textarea
                                        value={editEventData.description}
                                        onChange={(e) => setEditEventData({...editEventData, description: e.target.value})}
                                        className="w-full p-2 border border-gray-300 rounded"
                                        rows="3"
                                        placeholder="Event description..."
                                      />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Ticketing Agency</label>
                                        <select
                                          value={editEventData.ticketingAgency}
                                          onChange={(e) => setEditEventData({...editEventData, ticketingAgency: e.target.value})}
                                          className="w-full p-2 border border-gray-300 rounded"
                                        >
                                          <option value="Ticketmaster">Ticketmaster</option>
                                          <option value="Eventbrite">Eventbrite</option>
                                          <option value="Other">Other</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Ticket Link URL
                                          {editEventData.ticketingAgency === 'Other' && <span className="text-red-500"> *</span>}
                                        </label>
                                        <input
                                          type="url"
                                          value={editEventData.ticketLink}
                                          onChange={(e) => setEditEventData({...editEventData, ticketLink: e.target.value})}
                                          className="w-full p-2 border border-gray-300 rounded"
                                          placeholder={
                                            editEventData.ticketingAgency === 'Ticketmaster' 
                                              ? "Optional - Leave empty to use default Ticketmaster search"
                                              : editEventData.ticketingAgency === 'Eventbrite'
                                              ? "Optional - Leave empty to use default Eventbrite search" 
                                              : "https://..."
                                          }
                                          required={editEventData.ticketingAgency === 'Other'}
                                        />
                                      </div>
                                    </div>
                                    <div className="flex space-x-2">
                                      <button
                                        type="submit"
                                        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
                                      >
                                        Save Changes
                                      </button>
                                      <button
                                        type="button"
                                        onClick={handleCancelEdit}
                                        className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </form>
                                ) : (
                                  /* Display Mode for Past Events */
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <h4 className="font-semibold text-gray-700">{event.title}</h4>
                                      <p className="text-sm text-gray-500">{event.date} at {event.time} | {event.category}</p>
                                      <p className="text-sm text-gray-600">{event.ticketPrice}</p>
                                      <p className="text-xs text-gray-400 mt-1">Past Event</p>
                                    </div>
                                    <div className="flex space-x-2">
                                      <button
                                        onClick={() => handleEditEvent(event)}
                                        className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors text-sm"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => handleDeleteEvent(event.id)}
                                        className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition-colors text-sm"
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              {/* Add New Image Form */}
              <div className="mb-8 bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-bold mb-4">Add New Gallery Image</h3>
                <form onSubmit={handleAddImage} className="flex gap-4">
                  <input
                    type="url"
                    placeholder="Enter image URL (e.g., https://example.com/image.jpg)"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    className="flex-1 p-3 border border-gray-300 rounded"
                    required
                  />
                  <button
                    type="submit"
                    className="bg-red-600 text-white px-6 py-3 rounded font-bold hover:bg-red-700 transition-colors"
                  >
                    Add Image
                  </button>
                </form>
                <p className="text-sm text-gray-600 mt-2">
                  Tip: Upload images to a service like Imgur, Cloudinary, or your server, then paste the URL here.
                </p>
              </div>

              {/* Current Gallery Images */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold">Current Gallery Images ({galleryImages.length})</h3>
                  <button
                    onClick={handleResetGallery}
                    className="bg-gray-500 text-white px-4 py-2 rounded text-sm hover:bg-gray-600 transition-colors"
                  >
                    Reset to Default
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {galleryImages.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image}
                        alt={`Gallery ${index + 1}`}
                        className="w-full h-32 object-cover rounded border"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded flex items-center justify-center">
                        <button
                          onClick={() => handleRemoveImage(index)}
                          className="opacity-0 group-hover:opacity-100 bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {galleryImages.length === 0 && (
                  <p className="text-gray-500 text-center py-8">No images in gallery yet.</p>
                )}
                
                <div className="bg-green-50 p-4 rounded-lg mt-6">
                  <p className="text-green-800 text-sm">
                    ✅ <strong>Database Sync Enabled:</strong> All gallery changes are automatically saved to the database and synced across all devices. 
                    Images persist across browsers and devices, and are used as background wallpapers throughout the site.
                  </p>
                  <p className="text-green-700 text-xs mt-2">
                    💡 <strong>Tip:</strong> Use the "Reset to Default" button above to restore original images if needed.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'pageImages' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold">Page Image Management</h3>
              
              {/* Hero Section Image */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-4">Homepage Hero Section Background</h4>
                <div className="grid md:grid-cols-2 gap-4 items-start">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Image from Gallery
                    </label>
                    <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-300 rounded-lg p-2">
                      <div 
                        className={`cursor-pointer border-2 rounded-lg p-2 transition-colors ${
                          pageImages.hero === 'https://i.imgur.com/vi3Cqu1.jpeg' 
                            ? 'border-red-500 bg-red-50' 
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                        onClick={() => handlePageImageChange('hero', 'https://i.imgur.com/vi3Cqu1.jpeg')}
                      >
                        <div className="flex items-center space-x-3">
                          <img 
                            src="https://i.imgur.com/vi3Cqu1.jpeg" 
                            alt="Default Hero" 
                            className="w-16 h-12 object-cover rounded"
                          />
                          <span className="text-sm font-medium">Default Hero Image</span>
                        </div>
                      </div>
                      {galleryImages.map((imageUrl, index) => (
                        <div 
                          key={index}
                          className={`cursor-pointer border-2 rounded-lg p-2 transition-colors ${
                            pageImages.hero === imageUrl 
                              ? 'border-red-500 bg-red-50' 
                              : 'border-gray-200 hover:border-gray-400'
                          }`}
                          onClick={() => handlePageImageChange('hero', imageUrl)}
                        >
                          <div className="flex items-center space-x-3">
                            <img 
                              src={imageUrl} 
                              alt={`Gallery ${index + 1}`} 
                              className="w-16 h-12 object-cover rounded"
                            />
                            <span className="text-sm font-medium">Gallery Image {index + 1}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                    <img 
                      src={pageImages.hero} 
                      alt="Hero Preview" 
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                  </div>
                </div>
              </div>

              {/* FAQ Background Image */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-4">FAQ Page Background</h4>
                <div className="grid md:grid-cols-2 gap-4 items-start">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Image from Gallery
                    </label>
                    <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-300 rounded-lg p-2">
                      <div 
                        className={`cursor-pointer border-2 rounded-lg p-2 transition-colors ${
                          pageImages.faq === 'https://i.imgur.com/CvzwGq0.jpeg' 
                            ? 'border-red-500 bg-red-50' 
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                        onClick={() => handlePageImageChange('faq', 'https://i.imgur.com/CvzwGq0.jpeg')}
                      >
                        <div className="flex items-center space-x-3">
                          <img 
                            src="https://i.imgur.com/CvzwGq0.jpeg" 
                            alt="Default FAQ" 
                            className="w-16 h-12 object-cover rounded"
                          />
                          <span className="text-sm font-medium">Default FAQ Image</span>
                        </div>
                      </div>
                      {galleryImages.map((imageUrl, index) => (
                        <div 
                          key={index}
                          className={`cursor-pointer border-2 rounded-lg p-2 transition-colors ${
                            pageImages.faq === imageUrl 
                              ? 'border-red-500 bg-red-50' 
                              : 'border-gray-200 hover:border-gray-400'
                          }`}
                          onClick={() => handlePageImageChange('faq', imageUrl)}
                        >
                          <div className="flex items-center space-x-3">
                            <img 
                              src={imageUrl} 
                              alt={`Gallery ${index + 1}`} 
                              className="w-16 h-12 object-cover rounded"
                            />
                            <span className="text-sm font-medium">Gallery Image {index + 1}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                    <img 
                      src={pageImages.faq} 
                      alt="FAQ Preview" 
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                  </div>
                </div>
              </div>

              {/* Empress Tavern Image */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-4">Empress Tavern Content Image</h4>
                <div className="grid md:grid-cols-2 gap-4 items-start">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Image from Gallery
                    </label>
                    <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-300 rounded-lg p-2">
                      <div 
                        className={`cursor-pointer border-2 rounded-lg p-2 transition-colors ${
                          pageImages.empress === 'https://crestsacra1stg.wpenginepowered.com/wp-content/uploads/2020/03/photo-4-1024x768-1.jpg' 
                            ? 'border-red-500 bg-red-50' 
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                        onClick={() => handlePageImageChange('empress', 'https://crestsacra1stg.wpenginepowered.com/wp-content/uploads/2020/03/photo-4-1024x768-1.jpg')}
                      >
                        <div className="flex items-center space-x-3">
                          <img 
                            src="https://crestsacra1stg.wpenginepowered.com/wp-content/uploads/2020/03/photo-4-1024x768-1.jpg" 
                            alt="Default Empress" 
                            className="w-16 h-12 object-cover rounded"
                          />
                          <span className="text-sm font-medium">Default Empress Image</span>
                        </div>
                      </div>
                      {galleryImages.map((imageUrl, index) => (
                        <div 
                          key={index}
                          className={`cursor-pointer border-2 rounded-lg p-2 transition-colors ${
                            pageImages.empress === imageUrl 
                              ? 'border-red-500 bg-red-50' 
                              : 'border-gray-200 hover:border-gray-400'
                          }`}
                          onClick={() => handlePageImageChange('empress', imageUrl)}
                        >
                          <div className="flex items-center space-x-3">
                            <img 
                              src={imageUrl} 
                              alt={`Gallery ${index + 1}`} 
                              className="w-16 h-12 object-cover rounded"
                            />
                            <span className="text-sm font-medium">Gallery Image {index + 1}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                    <img 
                      src={pageImages.empress} 
                      alt="Empress Preview" 
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                  </div>
                </div>
              </div>

              {/* Rental Section Image */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-4">Rental Section Image</h4>
                <div className="grid md:grid-cols-2 gap-4 items-start">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Image from Gallery
                    </label>
                    <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-300 rounded-lg p-2">
                      <div 
                        className={`cursor-pointer border-2 rounded-lg p-2 transition-colors ${
                          pageImages.rental === 'https://i.imgur.com/b1k6F4U.jpeg' 
                            ? 'border-red-500 bg-red-50' 
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                        onClick={() => handlePageImageChange('rental', 'https://i.imgur.com/b1k6F4U.jpeg')}
                      >
                        <div className="flex items-center space-x-3">
                          <img 
                            src="https://i.imgur.com/b1k6F4U.jpeg" 
                            alt="Default Rental" 
                            className="w-16 h-12 object-cover rounded"
                          />
                          <span className="text-sm font-medium">Default Rental Image</span>
                        </div>
                      </div>
                      {galleryImages.map((imageUrl, index) => (
                        <div 
                          key={index}
                          className={`cursor-pointer border-2 rounded-lg p-2 transition-colors ${
                            pageImages.rental === imageUrl 
                              ? 'border-red-500 bg-red-50' 
                              : 'border-gray-200 hover:border-gray-400'
                          }`}
                          onClick={() => handlePageImageChange('rental', imageUrl)}
                        >
                          <div className="flex items-center space-x-3">
                            <img 
                              src={imageUrl} 
                              alt={`Gallery ${index + 1}`} 
                              className="w-16 h-12 object-cover rounded"
                            />
                            <span className="text-sm font-medium">Gallery Image {index + 1}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                    <img 
                      src={pageImages.rental} 
                      alt="Rental Preview" 
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-blue-800 text-sm">
                  💡 <strong>How it works:</strong> Click on any thumbnail to select it. The currently selected image has a red border. 
                  Changes are applied immediately across the website.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'banner' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold">Banner/Ribbon Management</h3>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-4">Banner Text & Styling</h4>
                
                {/* Banner Text Input */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Banner Text (Rich Text Supported)
                  </label>
                  <div
                    contentEditable
                    ref={(el) => {
                      // Only set initial content if element is empty and we have content
                      if (el && !el.innerHTML && bannerSettings.text) {
                        el.innerHTML = bannerSettings.text;
                      }
                    }}
                    onBlur={(e) => {
                      // Only update on blur to prevent cursor jumping during typing
                      const newContent = e.target.innerHTML;
                      if (newContent !== bannerSettings.text) {
                        handleBannerChange('text', newContent);
                      }
                    }}
                    onKeyDown={(e) => {
                      // Handle Enter key to prevent form submission
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        document.execCommand('insertHTML', false, '<br>');
                      }
                    }}
                    onPaste={(e) => {
                      // Allow pasting rich text
                      e.preventDefault();
                      const paste = e.clipboardData.getData('text/html') || e.clipboardData.getData('text/plain');
                      document.execCommand('insertHTML', false, paste);
                      
                      // Update state after paste
                      setTimeout(() => {
                        handleBannerChange('text', e.target.innerHTML);
                      }, 0);
                    }}
                    className="w-full p-3 border border-gray-300 rounded-lg min-h-[48px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{
                      backgroundColor: 'white',
                      color: 'black',
                      minHeight: '48px',
                      maxHeight: '120px',
                      overflowY: 'auto'
                    }}
                    suppressContentEditableWarning={true}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Supports rich text: paste formatted text with colors and links, or leave empty to hide banner
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    💡 <strong>Tip:</strong> You can paste text with colors, links, and formatting from other applications. Changes save when you click outside the field.
                  </p>
                </div>

                {/* Font Controls */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Font Family
                    </label>
                    <select
                      value={bannerSettings.fontFamily}
                      onChange={(e) => handleBannerChange('fontFamily', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                    >
                      <option value="Inter">Inter (Default)</option>
                      <option value="Dancing Script">Dancing Script (Script)</option>
                      <option value="Playfair Display">Playfair Display (Elegant)</option>
                      <option value="Arial">Arial</option>
                      <option value="Times New Roman">Times New Roman</option>
                      <option value="Georgia">Georgia</option>
                      <option value="Amasis MT Pro Black">Amasis MT Pro Black</option>
                      <option value="Baguet Script">Baguet Script</option>
                      <option value="Brush Script MT">Brush Script MT</option>
                      <option value="Cooper Black">Cooper Black</option>
                      <option value="Ink Free">Ink Free</option>
                      <option value="Stencil">Stencil</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Font Size (px)
                    </label>
                    <select
                      value={bannerSettings.fontSize}
                      onChange={(e) => handleBannerChange('fontSize', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                    >
                      <option value="16">16px (Small)</option>
                      <option value="18">18px</option>
                      <option value="20">20px</option>
                      <option value="22">22px</option>
                      <option value="24">24px (Default)</option>
                      <option value="26">26px</option>
                      <option value="28">28px</option>
                      <option value="32">32px (Large)</option>
                      <option value="36">36px (Extra Large)</option>
                    </select>
                  </div>
                </div>

                {/* Style Options */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Text Styling
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={bannerSettings.isBold}
                        onChange={(e) => handleBannerChange('isBold', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="font-bold">Bold</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={bannerSettings.isItalic}
                        onChange={(e) => handleBannerChange('isItalic', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="italic">Italic</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={bannerSettings.isUnderline}
                        onChange={(e) => handleBannerChange('isUnderline', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="underline">Underline</span>
                    </label>
                  </div>
                </div>

                {/* Banner Preview */}
                {(() => {
                  // Function to check if banner text is truly empty (handles HTML content)
                  const isBannerTextEmpty = (text) => {
                    if (!text) return true;
                    
                    // Create a temporary div to parse HTML and get text content
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = text;
                    const textContent = tempDiv.textContent || tempDiv.innerText || '';
                    
                    // Check if text content is empty after trimming
                    return textContent.trim() === '';
                  };

                  return bannerSettings.text && !isBannerTextEmpty(bannerSettings.text) ? (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Banner Preview
                      </label>
                      <div 
                        className="w-full py-4 px-6 text-center text-black shadow-lg"
                        style={{ 
                          backgroundColor: '#E9A319',
                          fontFamily: bannerSettings.fontFamily,
                          fontSize: `${bannerSettings.fontSize}px`,
                          fontWeight: bannerSettings.isBold ? 'bold' : 'normal',
                          fontStyle: bannerSettings.isItalic ? 'italic' : 'normal',
                          textDecoration: bannerSettings.isUnderline ? 'underline' : 'none'
                        }}
                        dangerouslySetInnerHTML={{ __html: bannerSettings.text || '' }}
                      />
                    </div>
                  ) : null;
                })()}

                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-blue-800 text-sm">
                    💡 <strong>How it works:</strong> Enter text to show a gold banner below the hero text. 
                    The banner only appears when text is entered. Choose font, size, and styling options. 
                    Changes apply immediately on the homepage.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'featured' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold">Featured Event Selection</h3>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Event to Feature on Homepage
                  </label>
                  <select
                    value={selectedFeaturedEventId || ''}
                    onChange={(e) => handleFeaturedEventChange(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                  >
                    <option value="">Auto-select (Next Upcoming Event)</option>
                    {(() => {
                      const { currentEvents } = categorizeEvents(events);
                      return currentEvents.map((event) => (
                        <option key={event.id} value={event.id}>
                          {event.title} - {new Date(event.date).toLocaleDateString()} at {event.time || '7:00 PM'}
                        </option>
                      ));
                    })()}
                  </select>
                  <p className="text-sm text-gray-500 mt-1">
                    Only current and upcoming events can be featured. Past events are not eligible.
                  </p>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-2">Current Featured Event:</h4>
                  <div className="text-blue-700">
                    <p className="font-medium">{featuredEvent.title}</p>
                    <p className="text-sm">
                      {new Date(featuredEvent.date).toLocaleDateString()} at {featuredEvent.time || '7:00 PM'}
                    </p>
                    <p className="text-sm text-blue-600 mt-1">
                      {selectedFeaturedEventId ? 'Manually Selected' : 'Auto-Selected (Next Event)'}
                    </p>
                  </div>
                </div>
                
                <div className="bg-yellow-50 p-4 rounded-lg mt-4">
                  <p className="text-yellow-800 text-sm">
                    💡 <strong>How it works:</strong> Select an event from the dropdown to feature it on the homepage. 
                    If "Auto-select" is chosen, the system will automatically show the next upcoming event.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contacts' && (
            <div className="space-y-6">
              <h3 className="text-lg font-bold">Contact Information</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    value={contacts.phone}
                    onChange={(e) => handleContactUpdate('phone', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">General Info Email</label>
                  <input
                    type="email"
                    value={contacts.generalInfoEmail || contacts.email || ''}
                    onChange={(e) => handleContactUpdate('generalInfoEmail', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rental/Booking Email</label>
                  <input
                    type="email"
                    value={contacts.rentalBookingEmail || ''}
                    onChange={(e) => handleContactUpdate('rentalBookingEmail', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                  <input
                    type="text"
                    value={contacts.address.street}
                    onChange={(e) => handleContactUpdate('address.street', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={contacts.address.city}
                    onChange={(e) => handleContactUpdate('address.city', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={contacts.address.state}
                    onChange={(e) => handleContactUpdate('address.state', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                  <input
                    type="text"
                    value={contacts.address.zip}
                    onChange={(e) => handleContactUpdate('address.zip', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                  />
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-700 mb-2">Social Media Links</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
                    <input
                      type="url"
                      value={contacts?.socialMedia?.facebook || ''}
                      onChange={(e) => handleContactUpdate('socialMedia.facebook', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Twitter</label>
                    <input
                      type="url"
                      value={contacts?.socialMedia?.twitter || ''}
                      onChange={(e) => handleContactUpdate('socialMedia.twitter', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">TikTok</label>
                    <input
                      type="url"
                      value={contacts?.socialMedia?.tiktok || ''}
                      onChange={(e) => handleContactUpdate('socialMedia.tiktok', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
                    <input
                      type="url"
                      value={contacts?.socialMedia?.instagram || ''}
                      onChange={(e) => handleContactUpdate('socialMedia.instagram', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Spotify</label>
                    <input
                      type="url"
                      value={contacts?.socialMedia?.spotify || ''}
                      onChange={(e) => handleContactUpdate('socialMedia.spotify', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-green-800 text-sm">
                  ✅ Contact information updates are applied immediately to the website.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'empress' && (
            <div>
              <h3 className="text-xl font-bold mb-6">Empress Tavern Content Management</h3>
              
              {/* Header Subtext */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Header Subtext (Center Aligned)
                </label>
                <input
                  type="text"
                  value={empressSettings.headerSubtext}
                  onChange={(e) => handleEmpressChange('headerSubtext', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  placeholder="Coming Soon - A Unique Sacramento Food Venue"
                />
                <p className="text-xs text-gray-500 mt-1">Text appears below the main header</p>
              </div>

              {/* Partnership Description */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Partnership Description (Center Aligned)
                </label>
                <div
                  contentEditable
                  dangerouslySetInnerHTML={{ __html: empressSettings.partnershipDescription || '' }}
                  onInput={(e) => handleEmpressChange('partnershipDescription', e.target.innerHTML)}
                  onBlur={(e) => handleEmpressChange('partnershipDescription', e.target.innerHTML)}
                  className="w-full p-3 border border-gray-300 rounded-lg h-24 overflow-y-auto focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{ 
                    minHeight: '96px',
                    backgroundColor: 'white'
                  }}
                  suppressContentEditableWarning={true}
                />
                <p className="text-xs text-gray-500 mt-1">Rich text supported - paste formatted text with colors, fonts, and links</p>
              </div>

              {/* Red Box Text */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Red Box Announcement (Center Aligned)
                </label>
                <div
                  contentEditable
                  dangerouslySetInnerHTML={{ __html: empressSettings.redBoxText || '' }}
                  onInput={(e) => handleEmpressChange('redBoxText', e.target.innerHTML)}
                  onBlur={(e) => handleEmpressChange('redBoxText', e.target.innerHTML)}
                  className="w-full p-3 border border-gray-300 rounded-lg h-24 overflow-y-auto focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{ 
                    minHeight: '96px',
                    backgroundColor: 'white'
                  }}
                  suppressContentEditableWarning={true}
                />
                <p className="text-xs text-gray-500 mt-1">Rich text supported - paste formatted text with colors, fonts, and links</p>
              </div>

              {/* White Box Text */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Information Box (Center Aligned)
                </label>
                <div
                  contentEditable
                  dangerouslySetInnerHTML={{ __html: empressSettings.whiteBoxText || '' }}
                  onInput={(e) => handleEmpressChange('whiteBoxText', e.target.innerHTML)}
                  onBlur={(e) => handleEmpressChange('whiteBoxText', e.target.innerHTML)}
                  className="w-full p-3 border border-gray-300 rounded-lg h-32 overflow-y-auto focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{ 
                    minHeight: '128px',
                    backgroundColor: 'white'
                  }}
                  suppressContentEditableWarning={true}
                />
                <p className="text-xs text-gray-500 mt-1">Rich text supported - paste formatted text with colors, fonts, and links</p>
              </div>

              {/* About Partnership */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  About Partnership Section (Left Aligned)
                </label>
                <div
                  contentEditable
                  dangerouslySetInnerHTML={{ __html: empressSettings.aboutPartnership || '' }}
                  onInput={(e) => handleEmpressChange('aboutPartnership', e.target.innerHTML)}
                  onBlur={(e) => handleEmpressChange('aboutPartnership', e.target.innerHTML)}
                  className="w-full p-3 border border-gray-300 rounded-lg h-40 overflow-y-auto focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{ 
                    minHeight: '160px',
                    backgroundColor: 'white'
                  }}
                  suppressContentEditableWarning={true}
                />
                <p className="text-xs text-gray-500 mt-1">Rich text supported - paste formatted text with colors, fonts, and links</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-green-800 text-sm">
                  🎨 <strong>Rich Text Editing:</strong> You can now paste formatted text directly from documents, websites, or design software. Colors, fonts, sizes, and links will be preserved exactly as you copied them!
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminInterface;